import { useT } from '@/i18n/useT';
import { errorMessage } from '@/lib/errorMessage';
import { useProStatus, useSetProAuto } from '@/bot/hooks';
import type { ProAsset } from '@/lib/api';

/**
 * Automatic Mode: what the 85-candle selector is doing, right now.
 *
 * WHY THIS EXISTS
 * The engine has chosen pairs, recorded its reasoning and refused bad payouts
 * since 2026-09-19, but none of it was visible. The client looked through every
 * screen on 2026-09-21 and reported he could find no Automatic Mode control and
 * no 85-candle information anywhere — correctly, because the gateway's read
 * allowlist did not carry /pro-status, so the dashboard was structurally unable
 * to show it.
 *
 * WHAT IT ANSWERS
 * The decision trail in History already says what happened AFTER a trade. The
 * question this answers is the one asked BEFORE one: which pairs are being
 * analysed, what is each one's trend and payout, which are eligible, and for
 * the rest — why not. A client watching a run of skipped signals should be able
 * to see that the payouts are below the floor rather than suspect a fault.
 *
 * THREE STATES, DELIBERATELY DISTINCT
 *   not provisioned  — this bot has no candle feed. No toggle is shown, because
 *                      a switch that cannot work is worse than none.
 *   provisioned, off — the analysis keeps running and stays visible; only the
 *                      pair SELECTION is paused. Turning it back on resumes
 *                      instantly, since the 85-bar window stayed warm.
 *   provisioned, on  — the selector chooses the pair for each scheduled signal.
 */
export function AutoModePanel() {
  const { t } = useT();
  const pro = useProStatus();
  const setProAuto = useSetProAuto();

  const data = pro.data;

  // THREE situations, three messages. Collapsing them into "not enabled" —
  // which the first version of this panel did — tells a customer whose bot is
  // simply DOWN that they do not have the feature. Same class of dishonesty as
  // the skipReason bug below: the panel exists to explain, so a wrong
  // explanation is worse than none.
  //
  // Mirrors BotGate's pattern and reuses its keys, so the wording and the
  // behaviour match the rest of the dashboard rather than inventing a dialect.
  if (pro.isLoading) {
    return (
      <section className="bot-card">
        <h2>{t('auto.title')}</h2>
        <p className="muted">{t('common.loading')}</p>
      </section>
    );
  }
  if (pro.isError || !data) {
    return (
      <section className="bot-card">
        <h2>{t('auto.title')}</h2>
        <p className="muted">{t('common.reconnecting')}</p>
      </section>
    );
  }
  // Only now is "not provisioned" the truth: the bot answered, and said it has
  // no candle feed.
  if (!data.enabled) {
    return (
      <section className="bot-card">
        <h2>{t('auto.title')}</h2>
        <p className="muted">{t('auto.notProvisioned')}</p>
      </section>
    );
  }

  const assets = data.assets ?? [];
  const selecting = data.selecting !== false;
  // Same trap as the row pill: `!a.skipReason` counts a bot that never sent
  // the field as "all eligible". Count only explicit nulls, so an older bot
  // reports 0 rather than inventing four.
  const eligible = data.eligibleCount ?? assets.filter((a) => a.skipReason === null).length;
  const windowBars = data.windowBars ?? 85;

  return (
    <section className="bot-card">
      <div className="bot-row-between">
        <h2>{t('auto.title')}</h2>
        <label className="bot-toggle">
          <input
            type="checkbox"
            checked={selecting}
            disabled={setProAuto.isPending}
            onChange={(e) => setProAuto.mutate(e.target.checked)}
          />
          <span>{selecting ? t('auto.on') : t('auto.off')}</span>
        </label>
      </div>

      {/* Separate singular keys rather than an ICU plural library: i18n/index.ts
          is dependency-free on purpose and defers ICU to Phase 8. Haitian Creole
          does not inflect nouns for number, so ht's two variants are identical by
          design - that is correct, not a copy-paste slip. */}
      <p className="muted bot-list-sub">
        {selecting
          ? t(assets.length === 1 ? 'auto.summaryOnOne' : 'auto.summaryOn', {
              eligible,
              total: assets.length,
              window: windowBars,
            })
          : t('auto.summaryOff')}
      </p>
      {/* The two payout floors, stated once. They are different gates and they
          stack: the selector will not CHOOSE a pair below minPayoutPct, and
          the execution guard refuses the CLICK below executionThresholdPct
          after re-reading the live payout. On this client's tenant they are 85
          and 80, so a pair between them is chosen and then refused — without
          both numbers that reads as a contradiction. Rendered only when the
          bot sent them, so an older bot shows nothing rather than a guess. */}
      {data.minPayoutPct != null && data.executionThresholdPct != null ? (
        <p className="muted bot-list-sub">
          {t('auto.floors', { min: data.minPayoutPct, exec: data.executionThresholdPct })}
        </p>
      ) : null}
      {setProAuto.isError ? (
        <p className="error">{errorMessage(setProAuto.error)}</p>
      ) : null}

      <ul className="bot-list">
        {assets.map((a) => (
          <AssetRow key={a.asset} a={a} />
        ))}
      </ul>
    </section>
  );
}

/** One pair: its trend, its payout, and the single reason it is or is not eligible. */
function AssetRow({ a }: { a: ProAsset }) {
  const { t } = useT();
  // `skipReason` is OPTIONAL: a bot on an older release omits it entirely
  // (verified — r13's proStatus has no skipReason, r14 does). `!a.skipReason`
  // would then be true for every pair and the panel would claim all four are
  // Eligible while the bot refuses to trade them, which is worse than saying
  // nothing. Only an explicit `null` means eligible; `undefined` means the
  // bot did not tell us, and the row says so.
  const known = a.skipReason !== undefined;
  const eligible = a.skipReason === null;
  // The label the client reads on the Pocket Option chart, not the wire symbol.
  const label = a.asset.replace(/_otc$/i, ' OTC').replace(/^([A-Z]{3})([A-Z]{3})/, '$1/$2');

  return (
    <li className="bot-list-item">
      <div>
        <div className="strong">{label}</div>
        <div className="muted bot-list-sub">
          {t('auto.trend')}: {a.trend === 'NONE' ? t('auto.trendNone') : a.trend}
          {a.payoutPct != null ? ` · ${t('auto.payout')}: ${a.payoutPct}%` : ''}
          {` · ${a.bars} ${t(a.bars === 1 ? 'auto.candle' : 'auto.candles')}`}
        </div>
        {/* The whole point of the panel: WHY a pair is not trading, before a
            signal fires rather than after it in History. */}
        {a.skipReason ? <div className="muted bot-list-sub">{a.skipReason}</div> : null}
      </div>
      <div className="bot-list-right">
        {known ? (
          <span className={`bot-pill ${eligible ? 'win' : 'skipped'}`}>
            {eligible ? t('auto.eligible') : t('auto.skipped')}
          </span>
        ) : (
          // Older bot: it streams and reports a trend, but cannot say whether
          // the pair is eligible. A dash is honest; a green "Eligible" is not.
          <span className="muted">—</span>
        )}
      </div>
    </li>
  );
}
